import api from '../api'; // Assuming a central api instance exists

/**
 * Fetches all clinical scoring configurations from the server.
 * @returns {Promise<Array>} A promise that resolves to an array of configuration objects.
 */
export const getAllConfigs = async () => {
  try {
    // Try the health-overview endpoint first (accessible to all authenticated users)
    const response = await api.get('/configs/health-overview');
    // The backend now returns a single object with config names as keys
    return response.data;
  } catch (error) {
    console.error('Error fetching clinical configurations:', error);

    // If it's a permission error (403), provide a more helpful error message
    if (error.response?.status === 403) {
      const permissionError = new Error('Access denied: Administrator privileges required to load health overview configurations. Please contact your system administrator.');
      permissionError.isPermissionError = true;
      throw permissionError;
    }

    // If it's an authentication error (401), provide a helpful message
    if (error.response?.status === 401) {
      const authError = new Error('Authentication required: Please log in again to access health overview data.');
      authError.isAuthError = true;
      throw authError;
    }

    throw error;
  }
};

/**
 * Updates a specific clinical scoring configuration.
 * @param {string} configName - The name of the configuration to update (e.g., 'scoring_config_labs').
 * @param {object} data - The new JSON data for the configuration.
 * @returns {Promise<object>} A promise that resolves to the updated configuration object.
 */
export const updateConfig = async (configName, data) => {
  try {
    const response = await api.put(`/configs/${configName}`, { data });
    return response.data;
  } catch (error) {
    console.error(`Error updating configuration ${configName}:`, error);
    throw error;
  }
};

export const getConfigByCategory = async (category) => {
  try {
    const categoryKey = category.toLowerCase().replace(' ', '_');
    const response = await api.get(`/configs/category/${categoryKey}`);
    return response.data;
  } catch (error) {
    console.error(`Error fetching configurations for category ${category}:`, error);
    throw error;
  }
};

/**
 * Fetches calculator settings (biological age method, etc.)
 * @returns {Promise<object>} A promise that resolves to the calculator settings object.
 */
export const getCalculatorSettings = async () => {
  try {
    const response = await api.get('/configs/calculator-settings');
    return response.data;
  } catch (error) {
    console.error('Error fetching calculator settings:', error);
    throw error;
  }
};

/**
 * Updates calculator settings
 * @param {object} settings - The calculator settings to update
 * @returns {Promise<object>} A promise that resolves to the updated settings object.
 */
export const updateCalculatorSettings = async (settings) => {
  try {
    const response = await api.put('/configs/calculator-settings', settings);
    return response.data;
  } catch (error) {
    console.error('Error updating calculator settings:', error);
    throw error;
  }
};
